/**
 * `GET`/`POST /consola/dispatch/parametros` — R-10 / CA-10.3, la configuración VERSIONADA que
 * gobierna cómo se reparten los viajes (`apps/api/src/dispatch/consola-parametros-dispatch.
 * controller.ts`).
 *
 * ⚠️ **Prefijo distinto al de `dispatchApi.ts`, a propósito.** La tasa de victoria vive en
 * `GET /dispatch/tasa-victoria` (sólo `JwtAuthGuard`) y esto vive bajo `consola/*` con el par
 * `JwtAuthGuard + OperadorGuard`, igual que los otros 8 controllers de la consola. **La anomalía
 * es aquél endpoint, no éste** — `dispatchApi.ts` ya lo tenía anotado ("es el único de los 8
 * endpoints de esta ronda que no pide `esAdminPlataforma`"). No se movió en esta ronda porque
 * mover una ruta ya publicada rompe al consumidor que existe; el controller nuevo documenta la
 * deuda. La pantalla queda igual detrás de `editar.dispatch`, así que el usuario no ve la costura.
 *
 * 🔴 **Los tipos NO son los de `dominio/contratos.ts`.** El mock `VersionParametros` declara
 * `editadoPor: string` y `motivo: string` OBLIGATORIOS; el backend real devuelve **`string |
 * null`** en los dos, porque las columnas `editadoPorId`/`motivo` de `ParametrosDispatch` son
 * nullables (la fila del seed no tiene autor). Reusar el tipo mock habría compilado igual y
 * roto en runtime al renderizar `null`. Mismo criterio que ya tomaron `verificacionApi.ts` e
 * `incidentesApi.ts` con sus propios tipos.
 */
import { obtener, postear } from './clienteApi';

/**
 * De dónde salieron los parámetros que el motor está usando AHORA.
 *
 * 🔴 `'FALLBACK'` no es un detalle interno: significa que **la tabla no está gobernando el
 * reparto** —no hay versión vigente, o la lectura falló— y que los viajes de ese rato quedan sin
 * `parametrosDispatchId`, o sea no atribuibles a ninguna versión (R-10). Sin este campo, "la
 * tabla dice 10" y "la tabla no respondió" devolverían el mismo objeto de números y el día que
 * el reparto cambiara solo, nadie tendría con qué darse cuenta.
 */
export type OrigenParametros = 'BD' | 'FALLBACK';

/** Lo que el motor de dispatch usa ahora mismo — la MISMA lectura que hace el dispatch. */
export interface ParametrosEfectivos {
  /** `null` cuando `origen === 'FALLBACK'`. */
  parametrosDispatchId: string | null;
  version: number;
  origen: OrigenParametros;
  radioBusquedaM: number;
  etaMaxSeg: number;
  ventanaOfertaSeg: number;
  nOfertaSimultanea: number;
  maxRondasOferta: number;
  frescuraPosicionMaxSeg: number;
}

/** Una fila del historial. Fechas serializadas como ISO string sobre HTTP (`Date` → string). */
export interface VersionParametrosReal {
  id: string;
  version: number;
  vigenteDesde: string;
  /** `null` = es la versión VIGENTE. Es así como la pantalla la identifica. */
  vigenteHasta: string | null;
  radioBusquedaM: number;
  etaMaxSeg: number;
  ventanaOfertaSeg: number;
  nOfertaSimultanea: number;
  maxRondasOferta: number;
  frescuraPosicionMaxSeg: number;
  /**
   * `null` cuando la versión no tiene autor registrado.
   *
   * ⚠️ **No se rellena con `'SEED'` ni con `'—'` en el cliente**: la columna no distingue "nació
   * con el sistema" de "alguien la escribió por fuera de la consola y no quedó registrado", y
   * poner el centinela del contrato acá inventaría una atribución sobre una versión que decide
   * el reparto de ingresos. Se muestra "sin registrar".
   */
  editadoPor: string | null;
  motivo: string | null;
}

export interface EstadoParametros {
  efectivos: ParametrosEfectivos;
  /** Historial completo, la más nueva primero. Vacío si la tabla nunca se escribió. */
  historial: VersionParametrosReal[];
}

/**
 * Los seis parámetros van SIEMPRE, aunque sólo cambie uno: el backend publica un SNAPSHOT
 * completo, no un parche. Si aceptara parches y heredara el resto de la versión vigente, entre
 * que el operador abrió la pantalla y apretó Guardar otro operador podría haber publicado, y la
 * versión quedaría firmada con valores que quien la firma nunca vio.
 */
export interface PublicarParametros {
  radioBusquedaM: number;
  etaMaxSeg: number;
  ventanaOfertaSeg: number;
  nOfertaSimultanea: number;
  maxRondasOferta: number;
  frescuraPosicionMaxSeg: number;
  /** Obligatorio, mínimo 10 caracteres — lo exige el backend (`@MinLength(10)`). */
  motivo: string;
}

export const obtenerParametrosDispatch = (): Promise<EstadoParametros> =>
  obtener<EstadoParametros>('/consola/dispatch/parametros');

/**
 * Publica una versión NUEVA. Nunca edita la vigente.
 *
 * Puede fallar con **409** si otra publicación se aplicó en simultáneo (`ErrorApi.status`): en
 * ese caso hay que releer el estado antes de reintentar, porque el borrador del operador se armó
 * sobre una versión que ya no es la vigente.
 */
export const publicarParametrosDispatch = (
  cuerpo: PublicarParametros,
): Promise<VersionParametrosReal> =>
  postear<VersionParametrosReal>('/consola/dispatch/parametros', cuerpo);
